import AuthService from '~/services/auth.service'
import memberhubConstants from '~/constants/memberhub.constants'
import { Capacitor } from '@capacitor/core'
import { InAppBrowser, DefaultSystemBrowserOptions } from '@capacitor/inappbrowser'
import isFlagOn from '~/composables/feature-flag'
import { FEATURE_FLAG } from '~/constants/feature-flags.constants'
import useDescopeWebAuth from '~/composables/use-descope-web-auth'
import isDescopeMfaEnabled from '~/composables/use-descope-mfa-flag'
import useMfaPostLoginGate from '~/composables/use-mfa-post-login-gate'
import useForceMfaFlag from '~/composables/use-force-mfa-flag'
import useMfaBypassFlag from '~/composables/use-mfa-bypass-flag'
import useMfaLogging, { MFA_EVENT, MFA_CONTEXT } from '~/composables/use-mfa-logging'

const authService = new AuthService()

/*
 * Used by finishSocialAuthentication() — the SSO-completing action that, unlike login(),
 * has no per-caller MFA gate wired in. Runs the exact same
 * enforcement rule as the legacy login form's runMfaGate() and the Descope-native login
 * page's proceedAfterFirstFactor(): Descope's own live verifiedPhone is the sole,
 * fail-closed trigger for a mandatory challenge; everything else (flag off, no Descope
 * session, Descope unreachable) fails open and leaves `path` untouched.
 *
 * This is the gate for EVERY SSO sign-in the native app makes, and for Facebook and Google
 * One-Tap on web — they all land on dashboard/authorization/callback.vue. It is the reason
 * enrolment no longer excludes password-less social accounts (see canEnrolMfa).
 *
 * @param {{store: object, email: string, path: string, query: object}} params
 * @returns {Promise<{path: string, query: object}>}
 */
async function resolveMfaAwarePath ({ store, email, path, query }) {
  const fallback = { path, query: query || {} }
  if (!email || !(await isDescopeMfaEnabled())) return fallback

  // Dedicated test accounts (CLUB-950): no challenge, no enrolment offer, and no Descope
  // round trip to find out whether either would have applied.
  if (useMfaBypassFlag().value) {
    useMfaLogging().logMfaEvent(MFA_EVENT.BYPASSED, { context: MFA_CONTEXT.LOGIN_CHALLENGE })
    return fallback
  }

  const { ensureDescopeSession, descopeAccessToken } = useDescopeSession()
  const refreshToken = await ensureDescopeSession(email)
  if (!refreshToken) return fallback

  const { getSecurityStatus } = useDescopeWebAuth()
  const securityStatus = await getSecurityStatus(descopeAccessToken.value)

  const profile = store.getUserProfile || {}
  if (profile.email === email) {
    store.setProfile({
      ...profile,
      descope: { ...(profile.descope || {}), ...securityStatus, smsMfaEnabled: !!securityStatus.verifiedPhone }
    })
  }

  if (securityStatus.verifiedPhone) {
    useMfaPostLoginGate().suspendForChallenge({ email, phone: securityStatus.phone, destination: fallback })
    return { path: '/dashboard/authorization/mfa-challenge/', query: {} }
  }

  /*
   * Not enrolled — offered enrollment, with skip visibility driven by Member Hub's own
   * force_MFA flag, no client-side flag, no per-provider exemption. A brand-new SSO account
   * lands here too: SSO has no separate registration step to hand it off from.
   *
   * allowSkip is resolved from the still-live profile BEFORE suspending: suspendAuthSession()
   * (inside suspendForEnrollment) wipes store.userProfile, so setup-mfa.vue has nothing left
   * to read force_MFA from once parked — it travels through the pending record instead.
   */
  const allowSkip = !useForceMfaFlag().value
  useMfaPostLoginGate().suspendForEnrollment({ email, phone: securityStatus.phone, destination: fallback, allowSkip })
  return { path: '/dashboard/setup-mfa/', query: {} }
}

const actions = {
  loader (status = true) {
    const layoutStore = useLayoutsStore()
    layoutStore.setPageLoader(status)
  },

  /*
   * Checking user exists or not by email or uuid
   * @param identifier (email or uuid)
   * @returns {Promise<unknown>}
  */
  async checkUserExistence (identifier) {
    this.loader(true)

    // Simple email regex for validation
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)
    // If not email, consider it UUID here (you can enhance this check if needed)
    const params = isEmail ? { email: identifier } : { uuid: identifier }

    const res = await authService.userExistenceCheck(params)
    return checkResponse(res, false)
      .then(response => {
        return Promise.resolve(response)
      })

      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },

  /*
     * Returns the appropriate authentication handler based on the response.
     * @param {Object} response - The response object containing authentication type and email.
     * @returns {Function|undefined} - The corresponding authentication handler function or undefined if not found.
     */
  getAuthenticationHandler (response) {
    const { $toast, $bus } = useNuxtApp()

    const authHandlers = {
      google: () => {
        this.socialAuthInit({ social_auth_type: memberhubConstants.GOOGLE_AUTH, email: response.email })
      },
      facebook: () => {
        this.socialAuthInit({ social_auth_type: memberhubConstants.FACEBOOK_AUTH, email: response.email })
      },
      linkedin: () => {
        this.socialAuthInit({ social_auth_type: memberhubConstants.LINKEDIN_AUTH, email: response.email })
      },
      apple: () => {
        this.socialAuthInit({ social_auth_type: memberhubConstants.APPLE_AUTH, email: response.email })
      },
      password: () => {
        $bus.emit('redirection', { path: generateLoginPageUrl() })
        $toast.error('Your password was already set. Please log in to continue.')
      }
    }

    return authHandlers[response.authentication]
  },

  /*
     * Constructs the password reset URL based on route query parameters.
     * @returns {string} - The constructed password reset URL.
     */
  resetUrl () {
    const route = useRoute()
    const { id, uuid, expires, token, signature } = route.query

    // Determine which identifier to use, with a check for both being undefined
    const userId = id || uuid

    return `/reset-password/${userId}?expires=${expires}&token=${token}&signature=${signature}`
  },

  /*
   * User registration store action
   * @param payload
   * @returns {Promise<unknown>}
   */
  async register (payload) {
    const { $tracking } = useNuxtApp()
    this.loader(true)
    const r = await authService.register(payload.formData)
    return checkResponse(r, false, payload.schema)
      .then(async response => {
        this.setSignUpData(payload.formData)
        $tracking.customEvent('My Account Sign up', { emailHashed: response.data?.hash_id })
        await this.login({ ...payload, ...{ preventRedirect: true } })
          .finally(() => {
            this.loader(false)
          })
        return Promise.resolve(response.data)
      })
      .catch(error => {
        this.loader(false)
        return Promise.reject(error)
      })
  },
  /*
     * User login store action
     * @param context
     * @param payload
     * @returns {Promise<unknown>}
     */
  async login (payload) {
    const { $tracking, $bus } = useNuxtApp()
    this.loader(true)
    const r = await authService.login(payload.formData)
    return checkResponse(r, false, payload.schema)
      .then(response => {
        this.setToken(response.data)
        this.setIsPurchasedSignUp(payload.psSignUp ? payload.psSignUp : false)
        return this.loadProfileAfterLogin()
          .then(async data => {
            if (import.meta.client) {
              sessionStorage.setItem('user_logged_in', true)
              $tracking.login({ emailHashed: data.hash_id })
            }
            await nextTick()

            /*
             * Optional post-authentication gate, used by the MFA challenge on the legacy
             * login screen. It runs after the profile has loaded but before the user is
             * sent anywhere, so a required second factor can be completed — or the session
             * torn back down — while they are still on the login form.
             *
             * Return value contract:
             *   undefined        -> nothing to do, redirect normally
             *   false            -> the gate owns navigation from here (e.g. it is showing
             *                       an OTP step); suppress the redirect entirely
             *   { path, query }  -> redirect there instead of the default destination
             * Throwing rejects login() so the caller can surface the error.
             */
            let redirectOverride
            if (typeof payload.onAuthenticated === 'function') {
              redirectOverride = await payload.onAuthenticated()
              if (redirectOverride === false) {
                $bus.emit('reset-login', true)
                return
              }
            }

            if (!payload.preventRedirect) {
              $bus.emit('redirection',
                redirectOverride || {
                  path: this.getAuthUserSourcePage || '/dashboard/',
                  query: this.getAuthUserSourcePageQuery || {}
                }
              )
            }
            $bus.emit('reset-login',true)
          })
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
     * User Profile store action
     * @param context
     * @param payload
     * @param skipBusEmit - Set to true to skip emitting logged-in event (for profile refreshes)
     * @returns {Promise<unknown>}
     */
  async profile (payload, skipBusEmit = false) {
    const { $tracking } = useNuxtApp()
    const formData = payload ? (payload.formData || {}) : {}
    formData.display_mode = $tracking?.getPWADisplayMode()
    const r = await authService.profile(formData)
    return checkResponse(r, false)
      .then(response => this.applyLoadedProfile(response.data, skipBusEmit))
  },
  /*
     * Post-login profile load. Calls /profile/sync instead of GET /profile: same
     * profile-shaped response, but Member Hub first refreshes its own record from Descope
     * (phone number, email/phone verified status), so every sign-in starts from a profile
     * that matches Descope rather than whatever Member Hub last stored.
     *
     * Falls back to the plain profile() fetch if the sync fails for any reason — Descope
     * unreachable, or an account Member Hub cannot sync — so a sync problem can never
     * block a sign-in. Deliberately sign-in only: registration still goes through
     * profile(), which is what creates Member Hub's record for a brand-new Descope user.
     * @param skipBusEmit - same as profile()
     * @returns {Promise<unknown>}
     */
  async loadProfileAfterLogin (skipBusEmit = false) {
    let data
    try {
      const response = await checkResponse(await authService.syncProfile(), false)
      data = response.data
    } catch {
      return this.profile(null, skipBusEmit)
    }
    return this.applyLoadedProfile(data, skipBusEmit)
  },
  /*
     * Shared tail of profile() and loadProfileAfterLogin(): stores the profile and runs
     * the side effects every profile load has always had.
     * @param data - profile-shaped response body
     * @param skipBusEmit - skip the "logged-in" event (for profile refreshes)
     * @returns {unknown} the profile
     */
  applyLoadedProfile (data, skipBusEmit) {
    /*
     * "logged-in" event -> For One-tap support
     * "authenticated" event -> global logged in watcher
     */
    if (!skipBusEmit) {
      useNuxtApp().$bus.emit('logged-in')
    }
    this.setProfile(data)
    // Fire device info sync in background (non-blocking)
    this.sendDeviceInfo().catch(() => {
      // Silently handle device info sync failures - don't block profile load
    })
    return data
  },
  /*
     * Send device info to dedicated endpoint
     * Used for app analytics and device tracking across iOS & Android native apps
     * @returns {Promise<unknown>}
     */
  async sendDeviceInfo () {
    const { $appDeviceInfo } = useNuxtApp()

    // Skip if no device info available (web/browser only)
    if (!$appDeviceInfo) {
      return Promise.resolve()
    }

    const payload = {
      app_build: $appDeviceInfo.appBuild,
      app_version: $appDeviceInfo.appVersion,
      device_model: $appDeviceInfo.deviceModel,
      display_mode: $appDeviceInfo.displayMode,
      manufacturer: $appDeviceInfo.manufacturer,
      os_version: $appDeviceInfo.osVersion,
      platform: $appDeviceInfo.platform,
      push_permission: $appDeviceInfo.pushPermission
    }

    const r = await authService.sendDeviceInfo(payload)
    return checkResponse(r, false)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        // Log but don't throw - device info sync failure shouldn't impact user flow
        // eslint-disable-next-line no-console
        console.warn('[User Store] Failed to sync device info:', error)
        return Promise.resolve()
      })
  },
  /*
     * User profile mutation
     *
     * Callers routinely PATCH a single field (phone_number, password, address...) and hand
     * whatever the API echoes back straight to this mutation — which used to replace the
     * whole profile verbatim. A narrow PATCH's response doesn't always echo every nested
     * field, so a same-user update that omits a nested `profile.*` field silently wiped it
     * from the store (and localStorage) even though nothing asked to change it. It once sent
     * fresh signups back to the (since removed) onboarding page after MFA setup:
     * setup-mfa.vue's own phone_number-only save (sent the instant an OTP is requested,
     * before enrolment even completes) clobbered a flag saved moments earlier. `descope`
     * already got this same protection; `profile` (the nested verification sub-object)
     * needs it too — merged rather than replaced,
     * so a field only the OLD response had survives, while anything the NEW response
     * actually sets (including a deliberate flip back to false, if that's ever real) wins.
     * @param profile
     */
  setProfile (profile) {
    const isSameUser = !!profile?.email && profile.email === this.userProfile?.email
    const nextProfile = profile && isSameUser
      ? {
        ...profile,
        descope: !profile.descope && this.userProfile?.descope ? this.userProfile.descope : profile.descope,
        profile: { ...(this.userProfile?.profile || {}), ...(profile.profile || {}) }
      }
      : profile
    setLocal('auth_user', JSON.stringify(nextProfile))
    this.userProfile = nextProfile
    useNuxtApp().$nativeAuthPersistence?.set('auth_user', JSON.stringify(nextProfile))
  },
  /*
     * Auth token mutation
     * @param token
     */
  setToken (token) {
    setLocal('token', JSON.stringify(token))
    this.token = token
    useNuxtApp().$nativeAuthPersistence?.set('token', JSON.stringify(token))
  },

  /*
     * Stores the Descope-issued session used to authorize MFA calls, WITHOUT touching
     * `token`. Only needed when the primary session is not itself Descope-issued — a
     * legacy Member Hub login — so that the MFA journey works independently of the
     * feature_DescopeAuthFlows flag. See use-descope-session.js.
     * @param token
     */
  setDescopeSession (token) {
    const value = token || {}
    setLocal('descope_session', JSON.stringify(value))
    this.descopeSession = value
    useNuxtApp().$nativeAuthPersistence?.set('descope_session', JSON.stringify(value))
  },

  /*
     * Clears only the Descope side-session, leaving the primary Member Hub session alone.
     */
  clearDescopeSession () {
    this.descopeSession = {}
    removeLocal('descope_session')
    useNuxtApp().$nativeAuthPersistence?.remove('descope_session')
  },

  /*
     * Records a challenge a login just raised: the session suspendAuthSession() parked,
     * the login id it's for, and where to send the user once verified. Persisted (not just
     * held in memory) because reaching it means navigating to the shared challenge page,
     * which can be a fresh page load.
     * @param {{suspended: object, email: string, destination: object|null}} payload
     */
  setPendingMfaChallenge (payload) {
    const value = payload || {}
    setLocal('pending_mfa_challenge', JSON.stringify(value))
    this.pendingMfaChallenge = value
    useNuxtApp().$nativeAuthPersistence?.set('pending_mfa_challenge', JSON.stringify(value))
  },

  /*
     * Clears a pending challenge once it's been verified or abandoned.
     */
  clearPendingMfaChallenge () {
    this.pendingMfaChallenge = {}
    removeLocal('pending_mfa_challenge')
    useNuxtApp().$nativeAuthPersistence?.remove('pending_mfa_challenge')
  },

  /*
     * Same as setPendingMfaChallenge, for mandatory enrolment instead of verification.
     * @param {{suspended: object, email: string, phone?: string, destination: object|null}} payload
     */
  setPendingMfaEnrollment (payload) {
    const value = payload || {}
    setLocal('pending_mfa_enrollment', JSON.stringify(value))
    this.pendingMfaEnrollment = value
    useNuxtApp().$nativeAuthPersistence?.set('pending_mfa_enrollment', JSON.stringify(value))
  },

  /*
     * Clears a pending enrolment once it's completed.
     */
  clearPendingMfaEnrollment () {
    this.pendingMfaEnrollment = {}
    removeLocal('pending_mfa_enrollment')
    useNuxtApp().$nativeAuthPersistence?.remove('pending_mfa_enrollment')
  },

  /*
     * Records a lost-phone recovery attempt: the session suspendAuthSession() parked, the
     * login id it's for, and where to send the user once the new phone verifies. Mirrors
     * setPendingMfaChallenge — persisted for the same reason (recovery spans full page
     * loads/entry points).
     * @param {{suspended: object, email: string, destination: object|null}} payload
     */
  setPendingMfaRecovery (payload) {
    const value = payload || {}
    setLocal('pending_mfa_recovery', JSON.stringify(value))
    this.pendingMfaRecovery = value
    useNuxtApp().$nativeAuthPersistence?.set('pending_mfa_recovery', JSON.stringify(value))
  },

  /*
     * Clears a pending recovery once the new phone number has verified, or the flow is
     * abandoned.
     */
  clearPendingMfaRecovery () {
    this.pendingMfaRecovery = {}
    removeLocal('pending_mfa_recovery')
    useNuxtApp().$nativeAuthPersistence?.remove('pending_mfa_recovery')
  },

  /*
     * Removes the live credential material from the store and from persistence, returning
     * it so it can be put back by restoreAuthSession().
     *
     * Used by the legacy-login MFA gate to fail CLOSED. login() necessarily commits the
     * session before the gate can run (profile() needs the token to load at all), so
     * without this a user who simply navigated away mid-challenge would be fully logged in
     * having never completed their second factor.
     *
     * Deliberately NOT clearAuthUser(): that also logs Braze out, resets the quote/
     * dashboard/rewards stores and drops unrelated local keys — side effects that are
     * correct for a real logout but wrong (and not cleanly reversible) for a few seconds
     * of held-back session during an OTP prompt.
     * @returns {{token: object, userProfile: object}}
     */
  suspendAuthSession () {
    const suspended = { token: this.token, userProfile: this.userProfile }

    this.token = {}
    this.userProfile = {}
    removeLocal('token')
    removeLocal('auth_user')
    useNuxtApp().$nativeAuthPersistence?.remove('token')
    useNuxtApp().$nativeAuthPersistence?.remove('auth_user')
    if (import.meta.client) {
      sessionStorage.removeItem('user_logged_in')
    }

    return suspended
  },

  /*
     * Reinstates a session parked by suspendAuthSession(), once the second factor passed.
     * @param suspended
     */
  restoreAuthSession (suspended) {
    if (!suspended?.token) return

    this.setToken(suspended.token)
    if (suspended.userProfile && Object.keys(suspended.userProfile).length) {
      this.setProfile(suspended.userProfile)
    }
    if (import.meta.client) {
      sessionStorage.setItem('user_logged_in', true)
    }
  },

  /*
     * Refreshes the auth token and stores the new tokens.
     * Returns the fresh access_token on success, or null on failure.
     * @returns {Promise<string|null>}
     */
  async refreshToken () {
    try {
      const res = await authService.refreshToken()
      const tokenData = res?.data?.value?.data || res?.data?.data || res?.data
      if (!tokenData?.access_token) return null

      // Preserve existing refresh_token if the refresh response returns empty or missing one
      const existingToken = this.token || {}
      const mergedToken = {
        ...existingToken,
        ...tokenData,
        refresh_token: tokenData.refresh_token || existingToken.refresh_token
      }
      this.setToken(mergedToken)
      return mergedToken.access_token
    } catch {
      return null
    }
  },

  /*
    * Auth token mutation
    * @param token
    */
  setIsPurchasedSignUp (psSignup) {
    this.psSignUp = psSignup
  },

  /*
     * Signup data mutation
     * @param payload
     */
  setSignUpData (payload) {
    this.signUpData = payload
  },
  /*
     * Initializing social authentication store action
     * @param context
     * @param payload
     * @returns {Promise<T>}
     */
  async socialAuthInit (payload) {
    this.loader(true)
    let r = null
    payload.state = this.defaultAuthState
    // eslint-disable-next-line default-case
    switch (payload.social_auth_type) {
      case memberhubConstants.GOOGLE_AUTH:
        r = await authService.googleAuthentication(payload)
        break
      case memberhubConstants.FACEBOOK_AUTH:
        r = await authService.facebookAuthentication(payload)
        break
      case memberhubConstants.LINKEDIN_AUTH:
        r = await authService.linkedinAuthentication(payload)
        break
      case memberhubConstants.APPLE_AUTH:
        r = await authService.appleAuthentication(payload)
        break
    }
    if (r) {
      return checkResponse(r, false)
        .then(async response => {
          if (import.meta.client && response.data.url) {
            if (Capacitor.isNativePlatform()) {
              // Use InAppBrowser with clear options
              await InAppBrowser.openInSystemBrowser({
                url: response.data.url,
                options: DefaultSystemBrowserOptions
              })
            } else {
              location.replace(response.data.url)
            }
            this.loader(false) // Hide loader after redirect
            return Promise.resolve(response.data)
          }
          return Promise.reject(response)
        })
        .catch(error => {
          this.loader(false)
          return Promise.reject(error)
        })
    }
    this.loader(false)
    return Promise.reject(r)
  },
  /*
     * Finishing social authentication store action
     * @param context
     * @param payload
     * @returns {Promise<any>}
     */
  async finishSocialAuthentication (payload) {
    const { $tracking } = useNuxtApp()
    try {
      const r = await authService.submitSocialAuth(payload.formData)
      const response = await checkResponse(r, false)

      this.setToken(response.data)
      //apple relay flag value in session storage
      if (import.meta.client) {
        if (response.data.is_apple_relay_email === true) {
          sessionStorage.setItem('is_apple_relay_email', 'true')
          // A brand-new relay account gets the one-time "Link your records" prompt on the
          // dashboard (AppleRelayPrompt); a returning one isn't asked again.
          if (response.data?.is_signup) {
            sessionStorage.setItem(memberhubConstants.APPLE_RELAY_LINK_PENDING_KEY, 'true')
          }
        } else {
          sessionStorage.removeItem('is_apple_relay_email')
        }
      }

      // Descope replaces the app's OAuth state with its own value, so the original
      // state (used as the sessionStorage key in setSourcePage) only survives via app_state.
      const stateKey = payload.formData?.app_state || payload.formData?.state

      // Extract session context
      const { sourcePageUrl, ssoUrl, sourcePagePath, ssoPath, sourcePageQuery } =
        readAuthSession(stateKey)

      // Track signups immediately
      if (response.data?.is_signup) {
        $tracking.customEvent('My Account Sign up', {
          emailHashed: response.data?.hash_id,
          sourcePageUrl,
          ssoUrl,
          sourcePagePath,
          ssoPath
        })
      }

      // Fetch profile — synced from Descope, see loadProfileAfterLogin
      const data = await this.loadProfileAfterLogin()

      // Side effects after login
      applyPostLoginSideEffects({
        store: this,
        tracking: $tracking,
        profileData: data,
        stateKey,
        trackingContext: { sourcePageUrl, ssoUrl, sourcePagePath, ssoPath }
      })

      const path = getRedirectionPath({
        sourcePageUrl,
        authRoutes: memberhubConstants.AUTH_ROUTES
      })

      // Descope requires no MFA check for the legacy Descope login/register flow's own
      // OAuth round trip (proceedAfterFirstFactor already covers it) — only for this SSO
      // callback, which previously had no MFA gate at all.
      const resolved = await resolveMfaAwarePath({
        store: this,
        email: data.email,
        path,
        query: sourcePageQuery || {}
      })

      // Navigate using Nuxt helper
      await navigateTo({ path: resolved.path, query: resolved.query })
    } catch (error) {
      await navigateTo(generateLoginPageUrl())
      this.loader(false)
      return Promise.reject(error)
    } finally {
      this.loader(false)
    }
  },
  /*
       * Forgot password
       * @param payload
       * @returns {Promise<T>}
       */
  async forgotPassword (payload) {
    this.loader(true)
    const r = await authService.forgotPassword(payload.formData)
    return checkResponse(r, true, payload.schema)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
     * Password reset store action
     * @param context
     * @param payload
     * @returns {Promise<unknown>}
     */
  async resetPassword (payload) {
    this.loader(true)
    const r = await authService.resetPassword(payload.url, payload.formData)
    return checkResponse(r, true, payload.schema)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },

  /*
   * Checking validity of url
   * @param identifier (email or uuid)
   * @returns {Promise<unknown>}
   */
  async resetPasswordCheck (url) {
    this.loader(true)

    const res = await authService.resetPasswordCheck(url)
    return checkResponse(res, false)
      .then(response => {
        return Promise.resolve(response)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },

  /*
     * User logout store action
     * @param context
     * @returns {Promise<void>}
     */
  // eslint-disable-next-line require-await
  async logout () {
    authService.logout(this.token?.refresh_token)

    /*
     * Best-effort Descope session revoke. There are two sessions that can need it:
     *
     *   - the primary token, when Descope issued it. Detected by `amr`, a field only a
     *     Descope-issued token carries (see toToken() in use-descope-web-auth.js), so a
     *     legacy-only session never attempts it.
     *   - the Descope side-session held alongside a Member Hub token on a legacy login.
     *     This one was previously never revoked: it is cleared locally by clearAuthUser(),
     *     but its refresh token stayed valid at Descope after the user logged out.
     *
     * Attempted when EITHER flag is on. Gating on feature_DescopeAuthFlows alone would
     * skip precisely the side-session this exists to clean up, since MFA can put a Descope
     * session on a user whose login never went through the Descope flow at all.
     *
     * Tokens are captured here, before forceLogout() clears them from the store. Nothing
     * below is awaited and every failure is swallowed — a slow, failing or misconfigured
     * Descope must never delay the local logout or surface an error to the user.
     */
    const descopeRefreshTokens = [...new Set([
      Array.isArray(this.token?.amr) ? this.token?.refresh_token : '',
      this.descopeSession?.refresh_token
    ].filter(Boolean))]

    if (descopeRefreshTokens.length) {
      Promise.all([
        isFlagOn(FEATURE_FLAG.descopeLogin),
        isDescopeMfaEnabled()
      ])
        .then(([descopeEnabled, mfaEnabled]) => {
          if (!descopeEnabled && !mfaEnabled) return

          const { logout: revokeDescopeSession } = useDescopeWebAuth()
          descopeRefreshTokens.forEach(token => {
            revokeDescopeSession(token).catch(() => {
              // Best-effort — the local session is cleared either way below.
            })
          })
        })
        .catch(() => {
          // A failed flag lookup must not surface during logout either.
        })
    }

    return this.forceLogout()
  },
  /*
     * Force logout store action
     * @param context
     * @returns {Promise<void>}
     */
  // eslint-disable-next-line require-await
  async forceLogout () {
    const { $toast } = useNuxtApp()
    this.clearAuthUser()
    $toast.success('Successfully logged out.')
    return Promise.resolve()
  },
  /*
     * Clear auth user mutation
     */
  clearAuthUser () {
    const layoutStore = useLayoutsStore()
    const vaultStore = useVaultStore()
    const quoteStore = useQuotesStore()
    const dashboardStore = useDashboardStore()
    const rewardsStore = useRewardsStore()
    this.userProfile = {}
    this.token = {}
    this.authModuleParams = {}
    this.descopeSession = {}
    this.pendingMfaChallenge = {}
    this.pendingMfaEnrollment = {}
    this.pendingMfaRecovery = {}
    removeLocal('token')
    removeLocal('auth_user')
    removeLocal('descope_session')
    removeLocal('pending_mfa_challenge')
    removeLocal('pending_mfa_enrollment')
    removeLocal('pending_mfa_recovery')
    useNuxtApp().$nativeAuthPersistence?.remove('token')
    useNuxtApp().$nativeAuthPersistence?.remove('auth_user')
    useNuxtApp().$nativeAuthPersistence?.remove('descope_session')
    useNuxtApp().$nativeAuthPersistence?.remove('pending_mfa_challenge')
    useNuxtApp().$nativeAuthPersistence?.remove('pending_mfa_enrollment')
    useNuxtApp().$nativeAuthPersistence?.remove('pending_mfa_recovery')
    removeLocal('verticals')
    removeLocal('existing_user')
    removeLocal('guestHealthQuotes')
    removeLocal('guestUserQuoteSaved')
    removeLocal('twaAppInit')
    removeLocal('capacitorBrazeUser')
    removeLocal('capacitorAppInit') // legacy one-shot flag, replaced by capacitorBrazeUser
    if (Capacitor.isNativePlatform()) {
      // Unsubscribe the current Braze user from push so a logged-out device
      // stops receiving their notifications; setUserId re-subscribes on login.
      useNuxtApp().$brazeBridge?.logout().catch(e => {
        // eslint-disable-next-line no-console -- intentional one-off diagnostic
        console.warn('BrazeBridge logout failed', e)
      })
    }
    layoutStore.resetPageLoader()
    vaultStore.resetProductList()
    // Clears the persisted SWR caches (dashboard cards, quotes, rewards) so
    // the next account on this device cold-starts instead of flashing stale
    // data.
    quoteStore.$reset()
    dashboardStore.$reset()
    rewardsStore.$reset()
    if (import.meta.client) {
      sessionStorage.removeItem('user_logged_in')
      // Clear the Apple relay-email flags so they don't leak into the next account
      // signed in on this tab (SSO login sets them; without this a subsequent regular
      // signup would wrongly get the "Link your records" prompt).
      sessionStorage.removeItem('is_apple_relay_email')
      sessionStorage.removeItem(memberhubConstants.APPLE_RELAY_LINK_PENDING_KEY)
    }
  },
  /*
     * User details updating  store action
     * @param context
     * @param payload
     * @returns {Promise<unknown>}
     */
  async saveUserProfileDetails (payload) {
    this.loader(true)
    const r = await authService.updateProfile(payload.formData)
    return checkResponse(r, payload.hasOwnProperty('showToast') ? payload.showToast : true, payload.schema)
      .then(response => {
        this.setProfile(response.data)
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },

  /*
     * Syncs Member Hub's own profile record from Descope's current state (email/phone
     * verified status) — Member Hub does the Descope lookup server-side from the caller's
     * own auth token; no body to send. The response is profile-shaped (plus a few extra
     * properties), so it's applied directly — no separate profile() refetch needed.
     * @returns {Promise<unknown>}
     */
  async syncProfileWithDescope () {
    const r = await authService.syncProfile()
    return checkResponse(r, false)
      .then(response => {
        this.setProfile(response.data)
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
  },

  /*
     * User password updating store action
     * @param context
     * @param payload
     * @returns {Promise<unknown>}
     */
  async saveUserPassword (payload) {
    this.loader(true)
    const r = await authService.updateProfilePassword(payload.formData)
    return checkResponse(r, payload.hasOwnProperty('showToast') ? payload.showToast : true, payload.schema)
      .then(response => {
        // The change ended every session, this one included; the backend hands back a
        // replacement so the member stays signed in (CLUB-946). Older backends return
        // no token and the old session keeps working until the next refresh.
        const { token, ...profile } = response.data || {}
        if (token?.access_token) {
          this.setToken(token)
        }
        this.setProfile(profile)
        return Promise.resolve(profile)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
     * User account update store action
     * @param context
     * @param payload
     * @returns {Promise<unknown>}
     */
  async deleteAccount (payload) {
    this.loader(true)
    const r = await authService.deleteAccount(payload.formData)
    return checkResponse(r, payload.hasOwnProperty('showToast') ? payload.showToast : true, payload.schema)
      .then(response => {
        this.clearAuthUser()
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
     * User email verification link resend store action
     * @returns {Promise<unknown>}
     */
  async resendVerification () {
    this.loader(true)
    const r = await authService.resendVerification()
    return checkResponse(r, true)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
     * Verification process store action with api url
     * @param context
     * @param callBackUrl
     * @returns {Promise<unknown>}
     */
  async verifyUserDirectly (callBackUrl) {
    this.loader(true)
    const r = await authService.verifyUser(callBackUrl)
    return checkResponse(r, true)
      .then(response => {
        this.profile()
          .then(() => {
            return Promise.resolve(response.data)
          })
          .catch(error => {
            return Promise.reject(error)
          })
          .finally(() => {
            this.loader(false)
          })
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
     * Mutation for reset User profile data
     * @param state
     */
  resetProfile () {
    const profile = getLocal('auth_user')
    this.userProfile = profile ? JSON.parse(profile) : {}
  },
  /*
     * Google one tap auth init store action
     * @param context
     * @param payload
     * @returns {Promise<T>}
     */
  async googleOneTapAuthInit (payload) {
    const res = await authService.googleOneTapAuthentication(payload)
    return checkResponse(res, false)
      .then(response => {
        if (import.meta.client && response.data.url) {
          location.replace(response.data.url)
          return Promise.resolve(response.data)
        }
        return Promise.reject(response)
      })
      .catch(error => {
        return Promise.reject(error)
      })
  },

  /*
     * Get user details store action
     * @returns {Promise<unknown>}
     */
  async getUserDetails () {
    const res = await authService.userDetails()
    return checkResponse(res, false)
      .then(response => {
        if (import.meta.client && response.data.user_id) {
          return Promise.resolve(response.data)
        }
        return Promise.reject(response)
      })
      .catch(error => {
        return Promise.reject(error)
      })
  },
  /*
   * Registers a guest user.
   *
   * @param {Object} payload - The payload containing the form data for guest registration.
   * @param {Object} payload.formData - The form data for guest registration.
   * @param {Object} [payload.profileDetails] - Quote answers saved to the new profile.
   * @returns {Promise<void>} - A promise that resolves when the guest user is registered.
   */
  async registerGuestUser (payload) {
    this.loader(true)
    const { $tracking, $toast } = useNuxtApp()
    const res = await authService.guestRegister(payload.formData)
    return checkResponse(res, false)
      .then(response => {
        this.setToken(response.data)
        nextTick().then(async () => {
          if (Object.keys(payload.profileDetails || {}).length) {
            await this.saveUserProfileDetails({ formData: payload.profileDetails, showToast: false })
          }
          return this.profile()
            .then(data => {
              if (import.meta.client) {
                sessionStorage.setItem('user_logged_in', true)
                $tracking.login({ emailHashed: data.hash_id })
                $toast.success('Check your inbox! We’ve sent you an email so you can set up your password and return to these results.')
              }
            })
        })
      })
      .catch(error => {
        return Promise.reject(new Error(error))
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Handles the guest password reset process.
   *
   * @param {Object} payload - The payload containing the form data for password reset.
   * @param {Object} payload.formData - The form data for password reset.
   * @returns {Promise<Object>} - A promise that resolves with the response data.
   */
  async guestPasswordResetHandler (payload) {
    this.loader(true)
    const res = await authService.guestPasswordReset(payload.formData)
    return checkResponse(res, true)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(new Error(error))
      })
      .finally(() => {
        this.loader(false)
      })
  },

  /*
    * Updates the Apple Relay email for the user.
    * @param {Object} payload - The payload containing the form data for updating the Apple Relay email.
    * @param {Object} payload.formData - The form data for updating the Apple Relay email.
    * @returns {Promise<Object>} - A promise that resolves with the response data.
    */
  async updateAppleRelayEmailHandler (payload) {
    this.loader(true)
    const res = await authService.updateAppleRelayEmail(payload.formData)
    return checkResponse(res, true, [])
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(new Error(error))
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
     * Mark modal as triggered and refresh profile
     * @param vaultId
     * @returns {Promise<unknown>}
     */
  async markModalTriggered (vaultId) {
    this.loader(true)
    const r = await authService.markModalTriggered(vaultId)
    return checkResponse(r, false)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  }
}
export default actions
